import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import HomepageBlocksApi from 'libs/api/website/homepageBlocks';
import AuthService from 'services/Auth';

const useHomepageBlocks = () => {
  const [homepageBlocks, setHomepageBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingShoppingCenters, setLoadingShoppingCenters] = useState(false);
  
  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchHomepageBlocks = useCallback(async (params = {}) => {
    if (!checkAuth()) return;

    setLoading(true);
    try {
      const response = await HomepageBlocksApi.list(params);
      
      if (response.data.success) {
        setHomepageBlocks(response.data.homepage_blocks || []);
        setTotal(response.data.pagination?.total_count || 0);
      } else {
        setHomepageBlocks([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching in evidenza blocks:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load in evidenza blocks');
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, navigate]);

  const fetchHomepageBlock = useCallback(async (id) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageBlocksApi.show(id);
      if (response.data.success) {
        return response.data.homepage_block;
      }
      return null;
    } catch (error) {
      console.error('Error fetching homepage block:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to access this page.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to load homepage block');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const createHomepageBlock = useCallback(async (data) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageBlocksApi.create(data);

      if (response.data.success) {
        return response.data.homepage_block || response.data;
      }

      const error = new Error(
        response.data.message || 'Failed to create homepage block',
      );
      error.response = { data: response.data };
      throw error;
    } catch (error) {
      console.error('Error creating homepage block:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to perform this action.');
        navigate('/login');
        return null;
      }
      throw error;
    }
  }, [checkAuth, navigate]);

  const updateHomepageBlock = useCallback(async (id, data) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageBlocksApi.update(id, data);

      if (response.data.success) {
        return response.data.homepage_block || response.data;
      }

      const error = new Error(
        response.data.message || 'Failed to update homepage block',
      );
      error.response = { data: response.data };
      throw error;
    } catch (error) {
      console.error('Error updating homepage block:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to perform this action.');
        navigate('/login');
        return null;
      }
      throw error;
    }
  }, [checkAuth, navigate]);

  const deleteHomepageBlock = useCallback(async (id) => {
    if (!checkAuth()) return false;

    try {
      const response = await HomepageBlocksApi.destroy(id);
      if (response.data.success) {
        message.success('Homepage block deleted successfully');
        return true;
      } else {
        message.error(response.data.message || 'Failed to delete homepage block');
        return false;
      }
    } catch (error) {
      console.error('Error deleting homepage block:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to perform this action.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to delete homepage block');
      }
      return false;
    }
  }, [checkAuth, navigate]);

  const uploadFile = useCallback(async (file) => {
    if (!checkAuth()) return null;

    try {
      const response = await HomepageBlocksApi.upload(file);
      if (response.data.success) {
        return response.data;
      } else {
        message.error(response.data.message || 'Failed to upload file');
        return null;
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      if (error.response?.status === 401) {
        message.error('You are not authorized to perform this action.');
        navigate('/login');
      } else {
        message.error(error.response?.data?.message || 'Failed to upload file');
      }
      return null;
    }
  }, [checkAuth, navigate]);

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;

    setLoadingShoppingCenters(true);
    try {
      const response = await HomepageBlocksApi.getFilters();
      if (response.data.success) {
        setShoppingCenters(response.data.shopping_centers || []);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
      message.error('Failed to load filters');
    } finally {
      setLoadingShoppingCenters(false);
    }
  }, [checkAuth]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) {
      setFilters({});
    } else {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    setCurrentPage(1); 
  }, []);

  const handleTableChange = useCallback((pagination, tableFilters, sorter) => {
    const newParams = {
      page: pagination.current,
      per: pagination.pageSize,
      ...tableFilters,
    };

    // Remove empty filters
    Object.keys(newParams).forEach(key => {
      if (newParams[key] === undefined || newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });

    paramsRef.current = newParams;
    fetchHomepageBlocks(newParams);
  }, [fetchHomepageBlocks]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    const params = {
      page: currentPage,
      per: pageSize,
      ...filters,
    };

    // Remove empty filters
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    });

    paramsRef.current = params;
    fetchHomepageBlocks(params);
  }, [currentPage, pageSize, filters, fetchHomepageBlocks]);

  return {
    homepageBlocks,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingShoppingCenters,
    fetchHomepageBlocks,
    fetchHomepageBlock,
    createHomepageBlock,
    updateHomepageBlock,
    deleteHomepageBlock,
    uploadFile,
    handleTableChange,
    updateFilters,
    setFilters,
  };
};

export default useHomepageBlocks;
